import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { FileText } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useGetDocumentDownloadUrlMutation } from '../../../hooks/api'
import { ImageViewer } from './image-viewer'
import { PdfViewer } from './pdf-viewer'

export interface PreviewDocument {
  id: string
  fileName: string
  mimeType: string | null
}

export interface DocumentPreviewModalProps {
  document: PreviewDocument
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional custom URL fetcher. When provided, overrides the default document download mutation. */
  fetchUrl?: (documentId: string) => Promise<string>
  /** Called when the PDF page changes (navigation or initial load). */
  onPageChange?: (page: number, totalPages: number) => void
}

function getViewerType(mimeType: string | null): 'pdf' | 'image' | 'unsupported' {
  if (!mimeType) return 'unsupported'
  if (mimeType === 'application/pdf') return 'pdf'
  if (mimeType.startsWith('image/')) return 'image'
  return 'unsupported'
}

export function DocumentPreviewModal({
  document,
  open,
  onOpenChange,
  fetchUrl,
  onPageChange,
}: DocumentPreviewModalProps) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [customPending, setCustomPending] = useState(false)
  const [customError, setCustomError] = useState(false)
  const {
    mutate: fetchDownloadUrl,
    isPending: defaultPending,
    isError: defaultError,
  } = useGetDocumentDownloadUrlMutation()

  const isPending = fetchUrl ? customPending : defaultPending
  const isError = fetchUrl ? customError : defaultError

  const fetchUrlRef = useRef(fetchUrl)
  fetchUrlRef.current = fetchUrl

  const fetchDefault = useCallback(
    (documentId: string) => {
      fetchDownloadUrl(
        { path: { id: documentId } },
        {
          onSuccess: (response) => {
            if (response.status === 'ok' && response.data?.downloadUrl) {
              setDocumentUrl(response.data.downloadUrl)
            }
          },
        }
      )
    },
    [fetchDownloadUrl]
  )

  useEffect(() => {
    if (!open || !document.id) return
    setDocumentUrl(null)
    setCustomPending(false)
    setCustomError(false)

    if (fetchUrlRef.current) {
      setCustomPending(true)
      fetchUrlRef
        .current(document.id)
        .then(setDocumentUrl)
        .catch(() => setCustomError(true))
        .finally(() => setCustomPending(false))
    } else {
      fetchDefault(document.id)
    }
  }, [open, document.id, fetchDefault])

  const viewerType = getViewerType(document.mimeType)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-7xl sm:max-w-7xl flex-col p-0 gap-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="truncate pr-8">{document.fileName}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
          {isPending && (
            <EmptyState
              icon={<FileText className="size-12 animate-pulse" />}
              title="Loading document..."
              dashed={false}
              className="border-none"
            />
          )}

          {isError && (
            <EmptyState
              icon={<FileText className="size-12" />}
              title="Failed to load document"
              dashed={false}
              className="border-none text-red-400"
            />
          )}

          {documentUrl && viewerType === 'pdf' && (
            <PdfViewer url={documentUrl} className="h-full w-full" onPageChange={onPageChange} />
          )}

          {documentUrl && viewerType === 'image' && (
            <ImageViewer url={documentUrl} alt={document.fileName} className="h-full" />
          )}

          {documentUrl && viewerType === 'unsupported' && (
            <EmptyState
              icon={<FileText className="size-12" />}
              title="Preview not available for this file type"
              dashed={false}
              className="border-none"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
